import React, {useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    cancelEventByList, CountEventWhiteList, GetEventApprovalComments,
    getEventDetail, GetEventSetting,
    GetUserInvitations, GetUserReservationSummary,
    GroupInvitationsByEventId
} from '../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi';
import { toast } from 'react-toastify';
import {calculateHeight, errorMsg, isSmWidth} from '../../../../Apis/functions';
import {USER_TYPE_MODEL} from "../../../../enums/userTypeEnum";
import {getReservationAttendanceByUserType} from "../Model/Model";
import {FILE_MODEL} from "../../Visitor/Visitor/Model/Model";
import {invitationInTime} from "../../Visitor/Invitation/Model/Model";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
/**
 * Hook component for managing event reservation details.
 * Handles fetching, updating, and cancellation of reservations and related invitations.
 *
 * @Author: Susana García Rodríguez
 * @JobTitle: Frontend - IBL
 * @Email: sgarcia@ibl.mx
 */
const ReservationDetailApp = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
     const DEV = useSelector((state) => state?.CommonsSlice?.development); // Fetch development flag from store
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Fetch user permissions from store
    const navigate = useNavigate();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [modified, setModified] = useState(false);
    const { eventDetail} = useSelector((state) => state?.EmployeeEventsSlice);
    const [statusId, setStatusId] = useState(eventDetail?.statusId);
    const [selectedTabId, setSelectedTabId] = useState(USER_TYPE_MODEL.EMPLOYEE);
    const currentTab = getReservationAttendanceByUserType(selectedTabId);
    const [assets, setAssets] = useState([]);
    const isOverdue = !invitationInTime(eventDetail?.endDate) ? STATUS_MODEL.OVERDUE : null;
    const [showManagerUserListModal, setShowManagerUserListModal] = useState(false);
    const [userClassificationSelected, setUserClassificationSelected] = useState(null);
    const containerRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState('auto');

    useEffect(() => {
        if (isSmWidth()) return;

        if (containerRef.current) {
            calculateHeight({
                elementRef: containerRef,
                bottom: 50,
                setHeight:setContainerHeight
            });
        }
    }, []);

    /**
     * Fetches event detail, comments, settings, invitations and summary.
     *
     * Side effects:
     * - Loads event detail by id
     * - Loads approval comments
     * - Loads event settings and maps files
     * - Loads invitations for selected tab
     * - Loads user reservation summary
     */
    useEffect(() => {
        dispatch(getEventDetail(id));
        dispatch(GetEventApprovalComments(id));
        dispatch(GetEventSetting()).then(({ payload: { data: { success, data } } }) => {
            if(success && data?.eventSettingFileDto?.length > 0){
                const aux = data.eventSettingFileDto.map(e => FILE_MODEL.set(e));
                setAssets(aux);
            }
        });
        getInvitations(selectedTabId);
        getSummary();
    }, []);

    /**
     * Refreshes invitations list when a modification occurs.
     *
     * Behavior:
     * - Reloads invitations for current tab
     * - Resets modified flag to false
     */
    useEffect(() => {
        if(modified){
            getInvitations(selectedTabId);
            getSummary();
            setModified(false)
        }
    }, [modified]);

    // Updates status ID when reservation data changes
    useEffect(() => {
        if (eventDetail?.statusId) {
            setStatusId(eventDetail.statusId);
        }
    }, [eventDetail]);

    /**
     * Fetches grouped and user-specific invitations by user type.
     *
     * @function getInvitations
     * @param {number} userTypeId - Identifier of the user type to filter invitations.
     */
    const getInvitations = (userTypeId) => {
        dispatch(GroupInvitationsByEventId(id));
        dispatch(GetUserInvitations({id, userTypeId}));
    }

    const getInvitationsTab = (userTypeId) => {
        dispatch(GetUserInvitations({id, userTypeId}));
    }

    const getSummary = () => {
        dispatch(GetUserReservationSummary(id));
        dispatch(CountEventWhiteList(id));
    }

    /**
     * Cancels the current event reservation and refreshes the data.
     * Displays success or error notifications accordingly.
     */
    const handleCancelEventByList = () => {
            dispatch(cancelEventByList([eventDetail.id])).then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    dispatch(getEventDetail(id));
                    setShowCancelModal(false);
                    toast.success(t('has_been_cancelled_successfully'));
                } else {
                    errorMsg(code);
                }
            });
        };

    return {
        t,
        navigate,
        id,
        isOverdue,
        setShowCancelModal,
        showCancelModal,
        statusId,
        DEV,
        permission,
        modified, setModified,
        selectedTabId,
        setSelectedTabId,
        currentTab,
        assets,
        showManagerUserListModal,
        setShowManagerUserListModal,
        userClassificationSelected,
        setUserClassificationSelected,
        handleCancelEventByList,
        getInvitationsTab,
        containerHeight,
        containerRef
    };
};

export default ReservationDetailApp;
